import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useAuth } from '../auth/useAuth';
import { listRallyes } from '../api/content';
import { listRuns, listSubmissions, listScores } from '../api/runs';
import { listUsers, listLogs } from '../api/admin';
import type { LogEntry, Run, RunState } from '../api/types';

const STATE_COLORS: Record<
  RunState,
  'default' | 'primary' | 'success' | 'warning' | 'error'
> = {
  configured: 'default',
  scheduled: 'primary',
  active: 'success',
  paused: 'warning',
  ended: 'error',
};

interface OrgAdminData {
  totalUsers: number;
  totalRallyes: number;
  totalRuns: number;
  activeRuns: Run[];
  recentLogs: LogEntry[];
}

interface CoordinatorData {
  activeRuns: Run[];
  unscoredCount: number;
}

const OrgAdminPanels: React.FC<{ orgSlug: string }> = ({ orgSlug }) => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [data, setData] = useState<OrgAdminData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgSlug) return;
    let cancelled = false;
    Promise.allSettled([
      listUsers(orgSlug, { limit: 1 }),
      listRallyes(orgSlug),
      listRuns(orgSlug),
      listLogs(orgSlug, { category: ['audit'], limit: 5 }),
    ])
      .then(([usersRes, rallyesRes, runsRes, logsRes]) => {
        if (cancelled) return;
        const totalUsers = usersRes.status === 'fulfilled' ? usersRes.value.total : 0;
        const totalRallyes =
          rallyesRes.status === 'fulfilled' ? rallyesRes.value.total : 0;
        const allRuns = runsRes.status === 'fulfilled' ? runsRes.value.items : [];
        const totalRuns = runsRes.status === 'fulfilled' ? runsRes.value.total : 0;
        const recentLogs = logsRes.status === 'fulfilled' ? logsRes.value.items : [];
        setData({
          totalUsers,
          totalRallyes,
          totalRuns,
          activeRuns: allRuns.filter((r) => r.state === 'active' || r.state === 'paused'),
          recentLogs,
        });
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [orgSlug]);

  if (error) return <Alert severity="warning">{t('dashboard.coordinator.loadFailed')}</Alert>;
  if (!data) return null;

  return (
    <Stack spacing={3} sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }} sx={{ display: 'flex' }}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="overline" sx={{
                color: "text.secondary"
              }}>
                {t('dashboard.stats.users')}
              </Typography>
              <Typography variant="h3" component="p">{data.totalUsers}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }} sx={{ display: 'flex' }}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="overline" sx={{
                color: "text.secondary"
              }}>
                {t('dashboard.stats.rallyes')}
              </Typography>
              <Typography variant="h3" component="p">{data.totalRallyes}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }} sx={{ display: 'flex' }}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="overline" sx={{
                color: "text.secondary"
              }}>
                {t('dashboard.stats.runs')}
              </Typography>
              <Typography variant="h3" component="p">{data.totalRuns}</Typography>
              <Typography variant="caption" sx={{
                color: "text.secondary"
              }}>
                {t('dashboard.stats.active', { count: data.activeRuns.length })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2" gutterBottom>
                {t('dashboard.activeRuns.title')}
              </Typography>
              {data.activeRuns.length === 0 ? (
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  {t('dashboard.activeRuns.empty')}
                </Typography>
              ) : (
                <List dense disablePadding>
                  {data.activeRuns.map((run) => (
                    <ListItem
                      key={run.id}
                      disablePadding
                      secondaryAction={
                        <Button size="small" onClick={() => navigate(`/runs/${run.id}`)}>
                          {t('dashboard.activeRuns.openDashboard')}
                        </Button>
                      }
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} sx={{
                            alignItems: "center"
                          }}>
                            <Typography variant="body2">{run.name}</Typography>
                            <Chip
                              label={run.state}
                              size="small"
                              color={STATE_COLORS[run.state]}
                            />
                          </Stack>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Stack
                direction="row"
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                <Typography variant="h6" component="h2">{t('dashboard.auditRecent.title')}</Typography>
                <Link
                  component="button"
                  variant="caption"
                  onClick={() => navigate('/admin/logs')}
                >
                  {t('dashboard.auditRecent.viewAll')}
                </Link>
              </Stack>
              {data.recentLogs.length === 0 ? (
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  {t('dashboard.auditRecent.empty')}
                </Typography>
              ) : (
                <List dense disablePadding>
                  {data.recentLogs.map((entry, idx) => (
                    <ListItem key={`${entry.timestamp ?? idx}-${idx}`} disablePadding>
                      <ListItemText
                        primary={entry.event ?? '—'}
                        secondary={
                          entry.timestamp
                            ? new Date(entry.timestamp).toLocaleString()
                            : ''
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
};

const CoordinatorPanels: React.FC<{ orgSlug: string }> = ({ orgSlug }) => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [data, setData] = useState<CoordinatorData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgSlug) return;
    let cancelled = false;
    (async () => {
      try {
        const runsRes = await listRuns(orgSlug);
        const activeRuns = runsRes.items.filter(
          (r) => r.state === 'active' || r.state === 'paused',
        );
        const unscoredPerRun = await Promise.all(
          activeRuns.map(async (r) => {
            const [subs, scores] = await Promise.all([
              listSubmissions(orgSlug, r.id),
              listScores(orgSlug, r.id),
            ]);
            const scored = new Set(scores.items.map((s) => s.submission_id));
            return subs.items.filter((s) => !scored.has(s.id)).length;
          }),
        );
        if (cancelled) return;
        setData({
          activeRuns,
          unscoredCount: unscoredPerRun.reduce((a, b) => a + b, 0),
        });
      } catch (err) {
        if (!cancelled) setError(String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgSlug]);

  if (error) return <Alert severity="warning">{t('dashboard.coordinator.loadFailed')}</Alert>;
  if (!data) return null;

  return (
    <Grid container spacing={2} sx={{ mt: 2 }}>
      <Grid size={{ xs: 12, md: 8 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom>
              {t('dashboard.activeRuns.title')}
            </Typography>
            {data.activeRuns.length === 0 ? (
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                {t('dashboard.activeRuns.empty')}
              </Typography>
            ) : (
              <List dense disablePadding>
                {data.activeRuns.map((run) => (
                  <ListItem
                    key={run.id}
                    disablePadding
                    secondaryAction={
                      <Button size="small" onClick={() => navigate(`/runs/${run.id}`)}>
                        {t('dashboard.activeRuns.openDashboard')}
                      </Button>
                    }
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} sx={{
                          alignItems: "center"
                        }}>
                          <Typography variant="body2">{run.name}</Typography>
                          <Chip
                            label={run.state}
                            size="small"
                            color={STATE_COLORS[run.state]}
                          />
                        </Stack>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="overline" sx={{
              color: "text.secondary"
            }}>
              {t('dashboard.coordinator.needsAttention')}
            </Typography>
            <Typography variant="h3" component="p">{data.unscoredCount}</Typography>
            <Typography variant="caption" sx={{
              color: "text.secondary"
            }}>
              {data.unscoredCount === 0
                ? t('dashboard.coordinator.noUnscored')
                : t('dashboard.coordinator.unscoredCount', { count: data.unscoredCount })}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const DefaultPanels: React.FC = () => {
  const { t } = useTranslation('auth');
  return (
    <Grid container spacing={3} sx={{ mt: 2 }}>
      <Grid size={{ xs: 12, md: 6, lg: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="h5" component="h2" gutterBottom>
              {t('dashboard.rallyes.title')}
            </Typography>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              {t('dashboard.rallyes.empty')}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 6, lg: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="h5" component="h2" gutterBottom>
              {t('dashboard.runs.title')}
            </Typography>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              {t('dashboard.runs.empty')}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 6, lg: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="h5" component="h2" gutterBottom>
              {t('dashboard.teams.title')}
            </Typography>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              {t('dashboard.teams.empty')}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation('auth');
  const orgSlug = user?.organization_slug ?? '';

  const panels = useMemo(() => {
    if (!user || !orgSlug) return <DefaultPanels />;
    if (user.role === 'org_admin') return <OrgAdminPanels orgSlug={orgSlug} />;
    if (user.role === 'coordinator') return <CoordinatorPanels orgSlug={orgSlug} />;
    return <DefaultPanels />;
  }, [user, orgSlug]);

  return (
    <Box>
      <Typography variant="h2" component="h1" gutterBottom>
        {t('dashboard.title')}
      </Typography>
      <Typography variant="body1" component="p" sx={{
        color: "text.secondary",
        mb: 2,
      }}>
        {t('dashboard.welcome', { name: user?.display_name })}
      </Typography>
      {panels}
    </Box>
  );
};

export default Dashboard;
